import { ReactElement, useMemo } from 'react';
import BigNumber from 'bignumber.js';

import { EpochTimer } from 'modules/common/components/EpochTimer';
import { IStatItemProps, StatItem } from 'modules/common/components/StatItem';
import { Stats as CommonStats } from 'modules/common/components/Stats';
import {
  globalTranslation,
  mergeTranslations,
  useTranslation,
} from 'modules/i18n';

import { useGetStatsQuery } from '../../actions/getStats';
import { translation } from './translation';

const mergedTranslation = mergeTranslations(globalTranslation, translation);

export function Stats(): ReactElement | null {
  const { t, keys } = useTranslation(mergedTranslation);

  const { data } = useGetStatsQuery();

  const tvl = data?.tvl;
  const rawTotalApy = data?.apyTotal;
  const apyTotal = useMemo(
    () =>
      rawTotalApy &&
      new BigNumber(rawTotalApy)
        .decimalPlaces(2, BigNumber.ROUND_HALF_EVEN)
        .toNumber(),
    [rawTotalApy],
  );
  const stakers = data?.stakers;

  const statsItems: IStatItemProps[] = useMemo(() => {
    return [
      {
        title: t(keys.tvl),
        value: tvl
          ? t(keys.unit.$, { value: t(keys.unit.compact, { value: tvl }) })
          : t(keys.common.none),
      },
      {
        title: t(keys.apy),
        value: apyTotal
          ? t(keys.unit.percent, { value: apyTotal })
          : t(keys.common.none),
      },
      {
        title: t(keys.stakers),
        value: stakers
          ? t(keys.unit.compact, { value: stakers })
          : t(keys.common.none),
      },
      {
        title: t(keys.epoch),
        value: <EpochTimer />,
      },
    ];
  }, [
    apyTotal,
    keys.apy,
    keys.common.none,
    keys.epoch,
    keys.stakers,
    keys.tvl,
    keys.unit.$,
    keys.unit.compact,
    keys.unit.percent,
    stakers,
    t,
    tvl,
  ]);

  return (
    <CommonStats>
      {statsItems.map(({ title, value }) => (
        <StatItem key={title} title={title} value={value} />
      ))}
    </CommonStats>
  );
}
